import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  heroImages,
  services,
  experiencePillars,
  whyChooseUs,
  founderInfo,
  testimonials
} from '../data/mock';
import { HeroQuoteWidget } from '../components/HeroQuoteWidget';
import { RecurringFamilyCTA } from '../components/RecurringFamilyCTA';
import { GoogleReviewCTA } from '../components/GoogleReviewCTA';

/* ── Animated Counter Hook ── */
const useCountUp = (target, duration = 2000) => {
  const [count, setCount] = useState(0);
  const startedRef = useRef(false);

  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);

  return { count, start };
};

export const Home = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const heroRef = useRef(null);
  const statsRef = useRef(null);

  const stat1 = useCountUp(500, 2200);
  const stat2 = useCountUp(24, 1800);
  const stat3 = useCountUp(49, 2000);

  useEffect(() => {
    setLoaded(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Trigger all counters when stats section is in view
  useEffect(() => {
    if (!statsRef.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          stat1.start();
          stat2.start();
          stat3.start();
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, [stat1, stat2, stat3]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('fade-in');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="home-page" className={loaded ? 'page-enter' : ''} style={{ backgroundColor: '#0D0D0D' }}>

      {/* ── HERO SECTION — LEFT-ALIGNED WITH CINEMATIC CSS ── */}
      <section
        data-testid="hero-section"
        ref={heroRef}
        className="relative overflow-hidden flex items-center"
        style={{ minHeight: '100vh' }}
      >
        {/* Base image with slow cinematic pan + zoom */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImages.home})`,
            filter: 'brightness(0.52) saturate(0.95) contrast(1.08)',
            animation: 'cinematicDrift 20s ease-in-out infinite alternate',
            transformOrigin: '30% 50%',
          }}
        />
        {/* Animated light sweep — simulates passing city lights */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(115deg, transparent 0%, transparent 35%, rgba(255,245,220,0.04) 45%, transparent 55%, transparent 100%)',
          animation: 'lightSweep 8s ease-in-out infinite',
          zIndex: 1,
        }} />
        {/* Warm ambient pulse — subtle life in the image */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 60% 55%, rgba(194,168,120,0.06) 0%, transparent 50%)',
          animation: 'ambientPulse 6s ease-in-out infinite alternate',
          zIndex: 1,
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 45%, rgba(0,0,0,0.7) 100%)',
          zIndex: 2,
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
          zIndex: 2,
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 20% 50%, rgba(194,168,120,0.03) 0%, transparent 60%)',
          zIndex: 2,
        }} />

        <div
          className="relative"
          style={{
            paddingLeft: 'clamp(24px, 5vw, 56px)',
            paddingRight: 'clamp(24px, 5vw, 48px)',
            paddingTop: '160px',
            paddingBottom: '120px',
            maxWidth: '780px',
            zIndex: 10,
          }}
        >
          <div style={{ marginBottom: '24px', animation: 'fadeIn 0.6s ease-out 0.2s both' }}>
            <div className="gold-divider" style={{ marginBottom: '20px' }} />
            <p style={{
              fontSize: '12px',
              color: 'rgba(194,168,120,0.55)',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              fontWeight: 300,
            }}>
              Boutique · Founder-Led · Family-Trusted
            </p>
          </div>

          <h1
            data-testid="hero-heading"
            className="font-serif"
            style={{
              fontSize: 'clamp(40px, 5vw, 64px)',
              fontWeight: 500,
              color: '#FAFAFA',
              lineHeight: 1.08,
              marginBottom: '28px',
              textShadow: '0 2px 40px rgba(0,0,0,0.5)',
              animation: 'fadeInUp 0.8s cubic-bezier(0.22,1,0.36,1) 0.3s both',
            }}
          >
            Family-First Chauffeur Service<br />Across the GTA
          </h1>

          <h2 data-testid="hero-subheading" className="font-serif" style={{
            fontSize: 'clamp(15px, 1.6vw, 19px)',
            color: '#C2A878',
            fontWeight: 400,
            letterSpacing: '0.5px',
            marginBottom: '20px',
            maxWidth: '560px',
            animation: 'fadeInUp 0.8s cubic-bezier(0.22,1,0.36,1) 0.4s both',
          }}>
            Trusted by Toronto, Oakville &amp; Milton families for school runs, airport transfers and quiet, private travel.
          </h2>

          <p style={{
            fontSize: '18px',
            color: 'rgba(220,220,220,0.62)',
            fontWeight: 300,
            lineHeight: 1.7,
            maxWidth: '520px',
            marginBottom: '36px',
            animation: 'fadeInUp 0.8s cubic-bezier(0.22,1,0.36,1) 0.5s both',
          }}>
            A boutique, founder-led service for households who prefer the same chauffeur every time — calm, discreet, and unhurried.
          </p>

          <div className="flex" style={{ gap: '16px', flexWrap: 'wrap', animation: 'fadeInUp 0.8s cubic-bezier(0.22,1,0.36,1) 0.7s both' }}>
            <button
              data-testid="hero-cta-btn"
              style={{
                backgroundColor: '#C2A878',
                color: '#0B0B0B',
                padding: '14px 28px',
                fontSize: '13px',
                fontWeight: 400,
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 300ms ease, box-shadow 300ms ease',
                boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
              onClick={() => navigate('/contact')}
            >
              Book Your Ride
            </button>
            <button
              data-testid="hero-services-btn"
              style={{
                backgroundColor: 'transparent',
                color: '#C2A878',
                padding: '14px 28px',
                fontSize: '13px',
                fontWeight: 400,
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                border: '1px solid rgba(194,168,120,0.35)',
                cursor: 'pointer',
                transition: 'all 300ms ease',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.08)'; e.target.style.borderColor = 'rgba(194,168,120,0.5)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.35)'; }}
              onClick={() => navigate('/services')}
            >
              Our Services
            </button>
          </div>

          {/* ── Embedded Quote Widget ── */}
          <HeroQuoteWidget />
        </div>

        <div className="absolute bottom-0 left-0 right-0" style={{
          height: '120px',
          background: 'linear-gradient(to top, #0D0D0D, transparent)',
          zIndex: 2,
        }} />
      </section>

      {/* ── Credentials Trust Strip — surfaced right below the hero ── */}
      <section data-testid="home-credentials-strip" className="relative" style={{ backgroundColor: '#0D0D0D', padding: '28px 24px', borderBottom: '1px solid rgba(194,168,120,0.06)' }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center" style={{ gap: '20px', textAlign: 'center' }}>
          <span style={{ fontSize: '10px', color: 'rgba(194,168,120,0.55)', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300, whiteSpace: 'nowrap' }}>
            Proud Member
          </span>
          <span className="hidden md:inline-block" style={{ width: '1px', height: '22px', background: 'rgba(194,168,120,0.2)' }} />
          <div className="flex flex-wrap items-center justify-center" style={{ gap: '14px' }}>
            <a href="https://www.limo.org/" target="_blank" rel="noopener noreferrer" data-testid="home-strip-nla" className="trust-badge" aria-label="Visit the National Limousine Association website" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '9px 18px', border: '1px solid rgba(194,168,120,0.22)', background: 'rgba(194,168,120,0.04)', textDecoration: 'none', cursor: 'pointer' }}>
              <img src="/badges/nla-white.webp" alt="National Limousine Association" width="46" height="17" loading="lazy" decoding="async" style={{ height: '17px', width: 'auto', opacity: 0.92 }} />
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(194,168,120,0.4)' }} />
              <span style={{ fontSize: '12px', color: 'rgba(220,220,220,0.7)', fontWeight: 300, letterSpacing: '0.3px' }}>National Limousine Association</span>
            </a>
            <a href="https://www.oloa.ca/" target="_blank" rel="noopener noreferrer" data-testid="home-strip-oloa" className="trust-badge" aria-label="Visit the Ontario Limousine Owners Association website" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '9px 18px', border: '1px solid rgba(194,168,120,0.22)', background: 'rgba(194,168,120,0.04)', textDecoration: 'none', cursor: 'pointer' }}>
              <span className="font-serif" style={{ fontSize: '15px', color: '#C2A878', letterSpacing: '3px', fontWeight: 500 }}>OLOA</span>
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(194,168,120,0.4)' }} />
              <span style={{ fontSize: '12px', color: 'rgba(220,220,220,0.7)', fontWeight: 300, letterSpacing: '0.3px' }}>Ontario Limousine Owners Association</span>
            </a>
          </div>
        </div>
      </section>


      {/* ── The Toronto Royale Experience ── */}
      <section data-testid="experience-section" className="relative overflow-hidden section-depth" style={{
        backgroundColor: '#0E0E0E',
        padding: '12rem 1.5rem',
      }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 20% 80%, rgba(194,168,120,0.05) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(194,168,120,0.03) 0%, transparent 50%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 15%, transparent 85%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-32 fade-element">
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '20px' }}>
              The Experience
            </p>
            <h2 data-testid="experience-heading" className="font-serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '24px' }}>
              How Families Travel With Us
            </h2>
            <div className="gold-divider mx-auto" style={{ marginBottom: '24px' }} />
            <p style={{ fontSize: '15px', color: 'rgba(225,225,225,0.85)', maxWidth: '500px', margin: '0 auto', fontWeight: 300, lineHeight: 1.7 }}>
              These aren't promises — they're how we work with our households, every day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ columnGap: '64px', rowGap: '72px' }}>
            {experiencePillars.map((pillar) => (
              <div key={pillar.title} className="fade-element group">
                <div
                  className="group-hover:border-[#C2A878]/50 transition-all duration-700"
                  style={{
                    borderLeft: '1px solid rgba(194,168,120,0.2)',
                    paddingLeft: '32px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                  }}
                >
                  <h3 className="font-serif" style={{ fontSize: '20px', fontWeight: 400, color: '#F5F5F5', marginBottom: '14px' }}>
                    {pillar.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', lineHeight: 1.9, fontWeight: 300 }}>
                    {pillar.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED TESTIMONIAL — Cinematic Full-Width ── */}
      <section data-testid="featured-testimonial" className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${heroImages.home})`,
          filter: 'brightness(0.12) saturate(0.5)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.6) 30%, rgba(10,10,10,0.6) 70%, rgba(10,10,10,0.95) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.04) 0%, transparent 50%)',
        }} />
        <div className="grain-overlay absolute inset-0" />

        <div className="max-w-4xl mx-auto relative z-10 text-center" style={{ padding: '8rem 1.5rem' }}>
          <div className="fade-element">
            <div style={{ width: '1px', height: '40px', backgroundColor: 'rgba(194,168,120,0.25)', margin: '0 auto 36px' }} />
            <p className="font-serif" style={{
              fontSize: 'clamp(22px, 3vw, 32px)', fontStyle: 'italic', color: 'rgba(245,245,245,0.7)',
              lineHeight: 1.7, fontWeight: 400, maxWidth: '700px', margin: '0 auto 36px',
            }}>
              &ldquo;{testimonials[0].content}&rdquo;
            </p>
            <div className="flex items-center justify-center" style={{ gap: '12px' }}>
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(194,168,120,0.3)' }} />
              <span style={{ fontSize: '13px', color: 'rgba(194,168,120,0.5)', letterSpacing: '1px', fontWeight: 300 }}>
                {testimonials[0].name} — {testimonials[0].role}
              </span>
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(194,168,120,0.3)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* ── ANIMATED STATS COUNTER ── */}
      <section data-testid="stats-section" className="relative overflow-hidden section-depth" style={{
        backgroundColor: '#0D0D0D',
        padding: '6rem 1.5rem',
      }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.03) 0%, transparent 50%)',
        }} />

        <div className="max-w-5xl mx-auto relative z-10">
          <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4" style={{ gap: '0' }}>
            {[
              { value: `${stat1.count}+`, label: 'Journeys Completed' },
              { value: `${stat2.count}/7`, label: 'Always Available' },
              { value: `${(stat3.count / 10).toFixed(1)}`, label: 'Client Satisfaction' },
              { value: 'Same-Day', label: 'Booking Available', isText: true },
            ].map((stat, i) => (
              <div
                key={stat.label}
                data-testid={`stat-${i}`}
                className="text-center fade-element"
                style={{
                  padding: '40px 24px',
                  borderRight: i < 3 ? '1px solid rgba(194,168,120,0.06)' : 'none',
                }}
              >
                <p className="font-serif" style={{
                  fontSize: stat.isText ? '28px' : 'clamp(36px, 4.5vw, 52px)',
                  fontWeight: 500,
                  color: '#C2A878',
                  marginBottom: '12px',
                  lineHeight: 1,
                  transition: 'color 300ms ease',
                }}>
                  {stat.value}
                </p>
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(204,204,204,0.45)',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontWeight: 300,
                }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OLOA Trust Strip ── */}
      <section data-testid="oloa-trust-strip" className="relative" style={{ backgroundColor: '#0A0A0A', padding: '48px 24px', borderTop: '1px solid rgba(194,168,120,0.06)', borderBottom: '1px solid rgba(194,168,120,0.06)' }}>
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-center" style={{ gap: '20px', textAlign: 'center' }}>
          <span style={{ fontSize: '10px', color: 'rgba(194,168,120,0.55)', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300, whiteSpace: 'nowrap' }}>
            Accredited Member
          </span>
          <span className="hidden md:inline-block" style={{ width: '1px', height: '22px', background: 'rgba(194,168,120,0.2)' }} />
          <a href="https://www.limo.org/" target="_blank" rel="noopener noreferrer" aria-label="Visit the National Limousine Association website" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <img src="/badges/nla-white.webp" alt="National Limousine Association" width="52" height="19" loading="lazy" decoding="async" style={{ height: '19px', width: 'auto', opacity: 0.92 }} />
            <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(194,168,120,0.4)' }} />
            <span style={{ fontSize: '13px', color: 'rgba(220,220,220,0.65)', fontWeight: 300, letterSpacing: '0.3px' }}>
              National Limousine Association
            </span>
          </a>
          <span className="hidden md:inline-block" style={{ width: '1px', height: '22px', background: 'rgba(194,168,120,0.2)' }} />
          <a href="https://www.oloa.ca/" target="_blank" rel="noopener noreferrer" aria-label="Visit the Ontario Limousine Owners Association website" style={{ display: 'inline-flex', alignItems: 'center', gap: '14px', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <span className="font-serif" style={{ fontSize: '22px', color: '#C2A878', letterSpacing: '4px', fontWeight: 500 }}>OLOA</span>
            <span style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'rgba(194,168,120,0.4)' }} />
            <span style={{ fontSize: '13px', color: 'rgba(220,220,220,0.65)', fontWeight: 300, letterSpacing: '0.3px' }}>
              Ontario Limousine Owners Association — Est. 1996
            </span>
          </a>
        </div>
      </section>

      {/* ── Why Clients Choose Us ── */}
      <section data-testid="why-section" className="relative overflow-hidden section-depth" style={{
        backgroundColor: '#0D0D0D',
        padding: '10rem 1.5rem',
      }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 80% 30%, rgba(194,168,120,0.03) 0%, transparent 60%)',
        }} />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-28 fade-element">
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '16px' }}>
              Why Toronto Royale
            </p>
            <h2 data-testid="why-heading" className="font-serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '24px' }}>
              Why Families Stay With Us
            </h2>
            <div className="gold-divider mx-auto" style={{ marginBottom: '24px' }} />
            <p style={{ fontSize: '16px', color: 'rgba(204,204,204,0.55)', maxWidth: '600px', margin: '0 auto', fontWeight: 300, lineHeight: 1.7 }}>
              Toronto Royale is intentionally small and personal. We cap daily bookings so every household receives the same chauffeur, the same care, and the same unhurried attention — every journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '64px' }}>
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center fade-element group">
                <div style={{ width: '1px', height: '32px', background: 'rgba(194,168,120,0.2)', margin: '0 auto 28px' }} className="group-hover:bg-[#C2A878]/40 transition-colors duration-700" />
                <h3 className="font-serif" style={{ fontSize: '20px', fontWeight: 400, color: '#F5F5F5', marginBottom: '16px' }}>
                  {item.title}
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', lineHeight: 1.8, fontWeight: 300 }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Recurring Family Schedule (one-click CTA) ── */}
      <RecurringFamilyCTA variant="section" testIdPrefix="home-recurring" />

      {/* ── Services Overview ── */}
      <section data-testid="services-overview-section" className="relative overflow-hidden section-depth" style={{
        backgroundColor: '#111111',
        padding: '10rem 1.5rem',
      }}>
        <div className="grain-overlay absolute inset-0" />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-28 fade-element">
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '16px' }}>
              Our Services
            </p>
            <h2 data-testid="services-heading" className="font-serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '16px' }}>
              Designed for What Matters
            </h2>
            <div className="gold-divider mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2" style={{ columnGap: '56px', rowGap: '80px' }}>
            {services.map((service) => (
              <div key={service.id} className="fade-element group">
                <div className="mb-8 overflow-hidden relative">
                  <div
                    className="bg-cover bg-center transition-transform duration-1000 group-hover:scale-105"
                    style={{
                      height: '340px',
                      backgroundImage: `url(${service.image})`,
                      filter: 'brightness(0.55) saturate(0.9)',
                    }}
                  />
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(180deg, transparent 50%, rgba(13,13,13,0.5) 100%)',
                  }} />
                </div>
                <h3 className="font-serif" style={{ fontSize: '22px', fontWeight: 400, color: '#F5F5F5', marginBottom: '14px' }}>
                  {service.title}
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', lineHeight: 1.8, fontWeight: 300, marginBottom: '20px' }}>
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center" style={{ gap: '12px' }}>
                      <span style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: 'rgba(194,168,120,0.35)', flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', color: 'rgba(225,225,225,0.75)', fontWeight: 300 }}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About the Founder ── */}
      <section data-testid="founder-section" className="relative overflow-hidden section-depth" style={{
        backgroundColor: '#0D0D0D',
        padding: '10rem 1.5rem',
      }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 75% 50%, rgba(194,168,120,0.03) 0%, transparent 60%)',
        }} />

        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center fade-element" style={{ marginBottom: '56px' }}>
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '16px' }}>
              From the Founder
            </p>
            <h2 data-testid="founder-heading" className="font-serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#F5F5F5', lineHeight: 1.15, marginBottom: '20px' }}>
              Built on Integrity
            </h2>
            <div className="gold-divider mx-auto" />
          </div>

          <div className="fade-element" style={{ maxWidth: '680px', margin: '0 auto' }}>
            <p style={{ fontSize: '15px', color: 'rgba(204,204,204,0.55)', lineHeight: 2, fontWeight: 300, marginBottom: '40px', textAlign: 'center' }}>
              {founderInfo.story}
            </p>
            <div style={{ borderLeft: '1px solid rgba(194,168,120,0.3)', paddingLeft: '32px', margin: '0 auto 40px', maxWidth: '560px' }}>
              <p className="font-serif" style={{ fontSize: '18px', color: 'rgba(245,245,245,0.75)', fontStyle: 'italic', lineHeight: 1.7, fontWeight: 400 }}>
                {founderInfo.mission}
              </p>
            </div>
            <p style={{ fontSize: '13px', color: 'rgba(194,168,120,0.4)', fontWeight: 300, textAlign: 'center', letterSpacing: '0.5px' }}>
              — {founderInfo.name}
            </p>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section data-testid="testimonials-section" className="relative overflow-hidden section-depth" style={{
        backgroundColor: '#111111',
        padding: '10rem 1.5rem',
      }}>
        <div className="grain-overlay absolute inset-0" />

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-28 fade-element">
            <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.55)', letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '16px' }}>
              Testimonials
            </p>
            <h2 data-testid="testimonials-heading" className="font-serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '16px' }}>
              What Clients Say
            </h2>
            <div className="gold-divider mx-auto" style={{ marginBottom: '16px' }} />
            <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.75)', fontWeight: 300, letterSpacing: '0.5px' }}>
              Trust built one journey at a time
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '40px' }}>
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="fade-element group">
                <div
                  style={{
                    height: '100%',
                    padding: '32px',
                    backgroundColor: 'rgba(26,26,26,0.35)',
                    border: '1px solid rgba(194,168,120,0.07)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                    transition: 'border-color 600ms ease, box-shadow 600ms ease, transform 400ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.16)';
                    e.currentTarget.style.boxShadow = '0 8px 36px rgba(0,0,0,0.25)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(194,168,120,0.07)';
                    e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ borderLeft: '1px solid rgba(194,168,120,0.2)', paddingLeft: '20px', marginBottom: '24px' }}>
                    <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.85)', lineHeight: 1.9, fontStyle: 'italic', fontWeight: 300 }}>
                      &ldquo;{testimonial.content}&rdquo;
                    </p>
                  </div>
                  <div style={{ paddingTop: '16px', borderTop: '1px solid rgba(194,168,120,0.06)' }}>
                    <p style={{ fontSize: '14px', color: 'rgba(245,245,245,0.75)', fontWeight: 400, letterSpacing: '0.3px' }}>{testimonial.name}</p>
                    <p style={{ fontSize: '12px', color: 'rgba(204,204,204,0.35)', fontWeight: 300, marginTop: '4px', letterSpacing: '0.3px' }}>{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Google Reviews CTA ── */}
      <GoogleReviewCTA testIdPrefix="home-reviews" />

      {/* ── CTA Section ── */}
      <section data-testid="cta-section" className="relative overflow-hidden section-depth" style={{
        backgroundColor: '#0D0D0D',
        padding: '10rem 1.5rem',
      }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.04) 0%, transparent 60%)',
        }} />

        <div className="max-w-3xl mx-auto text-center fade-element relative z-10">
          <div className="gold-divider mx-auto" style={{ marginBottom: '28px' }} />
          <h2 data-testid="cta-heading" className="font-serif" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 500, color: '#F5F5F5', marginBottom: '20px' }}>
            Let's Begin
          </h2>
          <p style={{ fontSize: '17px', color: 'rgba(225,225,225,0.8)', fontWeight: 300, marginBottom: '48px', lineHeight: 1.6 }}>
            I'd be honored to serve you.
          </p>
          <button
            data-testid="cta-get-in-touch-btn"
            style={{
              backgroundColor: '#C2A878',
              color: '#0B0B0B',
              padding: '15px 40px',
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 400ms ease',
              boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#D4BA8A';
              e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#C2A878';
              e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)';
            }}
            onClick={() => navigate('/contact')}
          >
            Get in Touch
          </button>
        </div>
      </section>
    </div>
  );
};
